import React, { useState } from 'react';
import Navigation from '../components/layout/Navigation';
import { motion } from 'framer-motion';

type UserType = 'rower' | 'family' | 'clubCoach' | 'collegeCoach';
type BillingPeriod = 'monthly' | 'annual';

interface PricingPlan {
  name: string;
  price: number;
  period: string;
  description: string;
  features: string[];
  cta: string;
  featured?: boolean;
  badge?: string;
}

const PricingPage: React.FC = () => {
  const [selectedUserType, setSelectedUserType] = useState<UserType>('rower');
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');

  const userTypes = [
    { id: 'rower' as UserType, label: 'Individual Rower', icon: '🚣' },
    { id: 'family' as UserType, label: 'Family', icon: '👨‍👩‍👧' },
    { id: 'clubCoach' as UserType, label: 'Club Coach', icon: '📋' },
    { id: 'collegeCoach' as UserType, label: 'College Coach', icon: '🎓' },
  ];

  const pricingPlans: Record<UserType, PricingPlan[]> = {
    rower: [
      {
        name: 'Recruit',
        price: billingPeriod === 'monthly' ? 9.99 : 99.0,
        period: billingPeriod === 'monthly' ? '/month' : '/year',
        description: 'Basic plan for rowers starting their college search',
        features: [
          'School tracking & favoriting',
          'Basic profile management',
          'Academic path tracking',
          'Recruitment path tracking',
          'Basic performance tracking',
          'Standard search filters',
        ],
        cta: 'Start Free Trial',
      },
      {
        name: 'Elite',
        price: billingPeriod === 'monthly' ? 19.99 : 199.0,
        period: billingPeriod === 'monthly' ? '/month' : '/year',
        description: 'Enhanced plan for serious recruits',
        features: [
          'Everything in Recruit plan',
          'Family member access (1)',
          'Personalized school recommendations',
          'Enhanced profile visibility',
          'Communication templates',
          'Recruitment calendar with alerts',
          'Application deadline tracking',
        ],
        cta: 'Start Free Trial',
        featured: true,
        badge: 'Most Popular',
      },
      {
        name: 'Performance',
        price: billingPeriod === 'monthly' ? 29.99 : 299.0,
        period: billingPeriod === 'monthly' ? '/month' : '/year',
        description: 'Premium plan for competitive athletes',
        features: [
          'Everything in Elite plan',
          'Telemetry data integration',
          'Performance analytics dashboard',
          'Peach/Concept2 data import',
          'Advanced statistics comparison',
          'Historical progression tracking',
          'College program matching algorithm',
        ],
        cta: 'Start Free Trial',
      },
    ],
    family: [
      {
        name: 'Family Basic',
        price: billingPeriod === 'monthly' ? 14.99 : 149.0,
        period: billingPeriod === 'monthly' ? '/month' : '/year',
        description: 'Basic plan for families with one rower',
        features: [
          'Single athlete account',
          'Parent dashboard access',
          'Basic profile management',
          'School tracking & favoriting',
          'Application progress tracking',
          'Basic notification system',
        ],
        cta: 'Start Free Trial',
      },
      {
        name: 'Family Plus',
        price: billingPeriod === 'monthly' ? 24.99 : 249.0,
        period: billingPeriod === 'monthly' ? '/month' : '/year',
        description: 'Enhanced plan for families with multiple rowers',
        features: [
          'Everything in Family Basic plan',
          'Multiple athlete accounts (up to 3)',
          'Enhanced parent dashboard',
          'Shared recruitment calendar',
          'Communication templates',
          'Deadline reminders',
          'Collaborative application tracking',
        ],
        cta: 'Start Free Trial',
        featured: true,
        badge: 'Best Value',
      },
      {
        name: 'Family Premier',
        price: billingPeriod === 'monthly' ? 34.99 : 349.0,
        period: billingPeriod === 'monthly' ? '/month' : '/year',
        description: 'Premium plan for serious rowing families',
        features: [
          'Everything in Family Plus plan',
          'Unlimited athlete accounts',
          'Full telemetry data integration',
          'Performance analytics dashboard',
          'Advanced program recommendations',
          'College visit planning tools',
          'Financial aid resources',
        ],
        cta: 'Start Free Trial',
      },
    ],
    clubCoach: [
      {
        name: 'Coach Essential',
        price: billingPeriod === 'monthly' ? 29.99 : 299.0,
        period: billingPeriod === 'monthly' ? '/month' : '/year',
        description: 'Basic plan for individual coaches',
        features: [
          'Athlete performance tracking (up to 10)',
          'Basic boat builder tool',
          'Lineup management',
          'Team schedule coordination',
          'College program database',
          'Compliance safeguards',
        ],
        cta: 'Start Free Trial',
      },
      {
        name: 'Coach Premium',
        price: billingPeriod === 'monthly' ? 69.99 : 699.0,
        period: billingPeriod === 'monthly' ? '/month' : '/year',
        description: 'Enhanced plan for established coaches',
        features: [
          'Everything in Coach Essential plan',
          'Up to 25 athletes',
          'Advanced boat builder',
          'Team performance analytics',
          'Bulk data upload for erg times',
          'Team progress visualization',
          'College recommendation tools',
          'Compliance automation',
        ],
        cta: 'Start Free Trial',
        featured: true,
      },
      {
        name: 'Club Pro',
        price: billingPeriod === 'monthly' ? 129.99 : 1299.0,
        period: billingPeriod === 'monthly' ? '/month' : '/year',
        description: 'Premium plan for entire rowing clubs',
        features: [
          'Everything in Coach Premium plan',
          'Unlimited athletes',
          'Multi-coach access',
          'Comprehensive program management',
          'Advanced telemetry integration',
          'Complete historical data analysis',
          'Custom reporting tools',
          'Priority technical support',
        ],
        cta: 'Contact Sales',
      },
    ],
    collegeCoach: [
      {
        name: 'Recruiter',
        price: billingPeriod === 'monthly' ? 99.99 : 999.0,
        period: billingPeriod === 'monthly' ? '/month' : '/year',
        description: 'Basic plan for assistant coaches',
        features: [
          'College coach verification',
          'Basic athlete search',
          'Limited profile viewing',
          'NCAA compliant messaging',
          'Basic recruitment tracking',
          'Simple reporting tools',
        ],
        cta: 'Contact Sales',
      },
      {
        name: 'Division III',
        price: billingPeriod === 'monthly' ? 199.99 : 1999.0,
        period: billingPeriod === 'monthly' ? '/month' : '/year',
        description: 'Enhanced plan for Division III programs',
        features: [
          'Everything in Recruiter plan',
          'Advanced athlete search filters',
          'Full profile access (with permissions)',
          'Recruitment pipeline management',
          'Athlete comparison tools',
          'Calendar management',
          'Team composition planning',
        ],
        cta: 'Contact Sales',
        featured: true,
      },
      {
        name: 'Division I/II',
        price: billingPeriod === 'monthly' ? 349.99 : 3499.0,
        period: billingPeriod === 'monthly' ? '/month' : '/year',
        description: 'Premium plan for Division I/II programs',
        features: [
          'Everything in Division III plan',
          'Elite prospect identification',
          'Advanced analytics dashboard',
          'Multi-year recruitment planning',
          'Historical performance analysis',
          'Automated compliance management',
          'Priority verified access',
        ],
        cta: 'Contact Sales',
      },
    ],
  };

  const plans = pricingPlans[selectedUserType];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isAuthenticated={false} />

      <section className="py-24 bg-gradient-to-b from-rowing-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl font-display font-bold text-rowing-navy">
              Plans That Grow With You
            </h1>
            <p className="mt-6 text-xl text-rowing-gray-600 max-w-3xl mx-auto">
              Whether you're an athlete, family, or coach, we have the perfect plan for your rowing
              recruitment journey.
            </p>
          </motion.div>

          {/* User Type Selector */}
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            {userTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setSelectedUserType(type.id)}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  selectedUserType === type.id
                    ? 'bg-rowing-blue text-white shadow-lg transform scale-105'
                    : 'bg-white text-rowing-gray-700 hover:bg-rowing-gray-50 shadow'
                }`}
              >
                <span className="mr-2 text-xl">{type.icon}</span>
                {type.label}
              </button>
            ))}
          </div>

          {/* Billing Period Toggle */}
          <div className="mt-8 flex justify-center items-center">
            <span
              className={`mr-3 ${billingPeriod === 'monthly' ? 'text-rowing-navy font-medium' : 'text-rowing-gray-600'}`}
            >
              Monthly
            </span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-rowing-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-rowing-blue focus:ring-offset-2"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  billingPeriod === 'annual' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span
              className={`ml-3 ${billingPeriod === 'annual' ? 'text-rowing-navy font-medium' : 'text-rowing-gray-600'}`}
            >
              Annual
              <span className="ml-1 text-sm text-rowing-blue">(Save up to 17%)</span>
            </span>
          </div>

          {/* Pricing Cards */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative bg-white rounded-2xl shadow-lg p-8 ${
                  plan.featured ? 'ring-2 ring-rowing-blue' : ''
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-rowing-blue text-white px-4 py-1 rounded-full text-sm font-semibold">
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="text-center">
                  <h3 className="text-2xl font-display font-semibold text-rowing-navy">
                    {plan.name}
                  </h3>
                  <div className="mt-4 flex items-baseline justify-center">
                    <span className="text-5xl font-bold text-rowing-navy">${plan.price}</span>
                    <span className="ml-1 text-rowing-gray-600">{plan.period}</span>
                  </div>
                  <p className="mt-4 text-rowing-gray-600">{plan.description}</p>
                </div>

                <ul className="mt-8 space-y-4">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <svg
                        className="w-5 h-5 text-rowing-blue mt-0.5 mr-3 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-rowing-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8">
                  <button className={`w-full ${plan.featured ? 'btn-primary' : 'btn-secondary'}`}>
                    {plan.cta}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Additional Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-16"
          >
            <div className="bg-rowing-gray-50 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-rowing-navy mb-4">All Plans Include:</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-start">
                  <svg
                    className="w-6 h-6 text-rowing-blue mr-3 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  <div>
                    <h4 className="font-medium text-rowing-navy">Secure & Private</h4>
                    <p className="text-sm text-rowing-gray-600">
                      Bank-level encryption and COPPA compliant
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg
                    className="w-6 h-6 text-rowing-blue mr-3 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <h4 className="font-medium text-rowing-navy">14-Day Free Trial</h4>
                    <p className="text-sm text-rowing-gray-600">No credit card required to start</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg
                    className="w-6 h-6 text-rowing-blue mr-3 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                  <div>
                    <h4 className="font-medium text-rowing-navy">Dedicated Support</h4>
                    <p className="text-sm text-rowing-gray-600">Get help when you need it most</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Add-ons Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-12"
          >
            <h3 className="text-2xl font-semibold text-rowing-navy text-center mb-8">
              Enhance Your Experience
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-6 shadow">
                <h4 className="font-medium text-rowing-navy mb-2">Additional Parent Access</h4>
                <p className="text-sm text-rowing-gray-600 mb-3">
                  Add more family members to your account
                </p>
                <p className="text-rowing-blue font-medium">$4.99/month per person</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow">
                <h4 className="font-medium text-rowing-navy mb-2">Data Export API</h4>
                <p className="text-sm text-rowing-gray-600 mb-3">
                  Export your data to external systems
                </p>
                <p className="text-rowing-blue font-medium">$9.99/month</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow">
                <h4 className="font-medium text-rowing-navy mb-2">Custom Branding</h4>
                <p className="text-sm text-rowing-gray-600 mb-3">
                  Customize with your program colors and logo
                </p>
                <p className="text-rowing-blue font-medium">$49.99/month</p>
              </div>
            </div>
          </motion.div>

          {/* Discounts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-12 text-center"
          >
            <p className="text-rowing-gray-600">
              <span className="font-medium">Special Discounts Available:</span> Academic
              institutions (15% off), Non-profit clubs (20% off), Multi-team organizations (15% off)
            </p>
            <p className="mt-4">
              Questions?{' '}
              <button className="text-rowing-blue hover:underline font-medium">
                Contact our team
              </button>
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
